/**
 * AppShell — The authenticated app layout with sidebar navigation
 *
 * Sub-pages: Profile Editor, Scan Page Preview, QR Card, Scans/CRM, Referrals
 *
 * Features:
 * - Unsaved changes warning when switching tabs from editor
 * - First-save social share popup (Instagram, Facebook, LinkedIn)
 * - Referral system with share tracking and points
 *
 * API_TODO:
 * - GET /api/users/me → fetch user data on mount
 * - Auth token verification on mount (redirect to /auth if invalid)
 */
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AscendingK from '../components/AscendingK';
import { Icons } from '../components/Icons';
import ProfileEditor from './ProfileEditor';
import ScanPagePreview from './ScanPagePreview';
import CardPage from './CardPage';
import ScansPage from './ScansPage';
import ReferralsPage from './ReferralsPage';
import HelpPage from './HelpPage';
import SharePopup from '../components/SharePopup';
import OnboardingTour from '../components/OnboardingTour';
import FeedbackPopup, { shouldShowFeedback } from '../components/FeedbackPopup';
import { useAuth } from '../context/AuthContext';

export default function AppShell() {
  const navigate = useNavigate();
  const { user, setUser, logout } = useAuth();
  const [page, setPage] = useState('editor');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [editorDirty, setEditorDirty] = useState(false);
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [showUnsavedAlert, setShowUnsavedAlert] = useState(false);
  const [pendingPage, setPendingPage] = useState(null);

  // Onboarding: show once on first login
  const [showOnboarding, setShowOnboarding] = useState(() => {
    // API_TODO: Check user.onboardingComplete from backend instead of localStorage
    return !localStorage.getItem('connektedin_onboarding_done');
  });

  // Feedback popup: show after 2+ days of first use
  const [showFeedback, setShowFeedback] = useState(() => {
    // API_TODO: Check user.feedbackSubmittedAt from backend
    return shouldShowFeedback();
  });

  if (!user) {
    return null;
  }

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    localStorage.setItem('connektedin_onboarding_done', 'true');
    // API_TODO: PUT /api/users/me { onboardingComplete: true }
  };

  const newScansCount = user.scans.filter((s) => s.list === 'new').length;

  const navItems = [
    { key: 'editor', label: 'Profile Editor', icon: Icons.edit },
    { key: 'preview', label: 'Scan Page Preview', icon: Icons.eye },
    { key: 'card', label: 'Your QR Card', icon: Icons.scan },
    { key: 'scans', label: 'Scan Notifications', icon: Icons.bell, badge: newScansCount },
    { key: 'referrals', label: 'Referrals', icon: Icons.external },
    { key: 'help', label: 'Help & Support', icon: Icons.help },
  ];

  // Handle dirty state from ProfileEditor
  const handleDirtyChange = useCallback((dirty) => {
    setEditorDirty(dirty);
  }, []);

  // Handle save success from ProfileEditor
  const handleSaveSuccess = useCallback((type) => {
    if (type === 'first') {
      setShowSharePopup(true);
    }
  }, []);

  // Navigate with unsaved changes check
  const handleNavClick = (key) => {
    if (page === 'editor' && editorDirty && key !== 'editor') {
      setPendingPage(key);
      setShowUnsavedAlert(true);
      return;
    }
    setPage(key);
    setSidebarOpen(false);
  };

  const confirmLeave = () => {
    setEditorDirty(false);
    setShowUnsavedAlert(false);
    if (pendingPage) {
      setPage(pendingPage);
      setPendingPage(null);
    }
    setSidebarOpen(false);
  };

  const cancelLeave = () => {
    setShowUnsavedAlert(false);
    setPendingPage(null);
  };

  const handleLogout = () => {
    if (editorDirty) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to leave?')) return;
    }
    logout();
    navigate('/');
  };

  return (
    <div className="app-shell">
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <AscendingK size={22} color="#C9A961" />
          <span className="sidebar-brand">
            connekted <em>in</em>
          </span>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <button
              key={item.key}
              className={`nav-item ${page === item.key ? 'active' : ''}`}
              onClick={() => handleNavClick(item.key)}
            >
              {item.icon}
              {item.label}
              {item.badge > 0 && <span className="badge">{item.badge}</span>}
            </button>
          ))}
          <div style={{ flex: 1 }} />
          <button className="nav-item" onClick={handleLogout}>
            {Icons.home} Back to site
          </button>
        </nav>

        <div className="sidebar-footer">v1.0 &middot; Phase 1</div>
      </aside>

      {/* Main content */}
      <main className="main-content">
        {/* Mobile menu toggle */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="mobile-menu-btn"
          style={{
            display: 'none',
            position: 'fixed',
            top: 16,
            left: 16,
            zIndex: 200,
            background: 'var(--brand)',
            color: 'var(--paper)',
            border: 'none',
            padding: '8px 12px',
            cursor: 'pointer',
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 14,
            borderRadius: 'var(--r-sm)',
          }}
        >
          {sidebarOpen ? 'Close' : 'Menu'}
        </button>

        {page === 'editor' && (
          <ProfileEditor
            user={user}
            setUser={setUser}
            onDirtyChange={handleDirtyChange}
            onSaveSuccess={handleSaveSuccess}
          />
        )}
        {page === 'preview' && <ScanPagePreview user={user} />}
        {page === 'card' && <CardPage user={user} />}
        {page === 'scans' && <ScansPage user={user} setUser={setUser} />}
        {page === 'referrals' && <ReferralsPage user={user} />}
        {page === 'help' && <HelpPage user={user} />}
      </main>

      {/* Unsaved changes modal */}
      {showUnsavedAlert && (
        <div className="modal-overlay" onClick={cancelLeave}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <span className="modal-title">Unsaved changes</span>
              <button className="btn btn-ghost-app" onClick={cancelLeave}>{Icons.close}</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 15, color: 'var(--muted)', lineHeight: 1.6 }}>
                You have unsaved changes in the Profile Editor. Please click <strong>Save &amp; Publish</strong> to apply your changes before switching tabs.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost-app btn-sm" onClick={confirmLeave}>
                Discard changes
              </button>
              <button className="btn btn-primary-app btn-sm" onClick={cancelLeave}>
                Go back &amp; save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* First-save social share popup */}
      {showSharePopup && (
        <SharePopup user={user} onClose={() => setShowSharePopup(false)} />
      )}

      {/* Onboarding tour (first login only) */}
      {showOnboarding && (
        <OnboardingTour onComplete={handleOnboardingComplete} />
      )}

      {/* Feedback popup (after 2+ days) */}
      {showFeedback && !showOnboarding && (
        <FeedbackPopup
          onSubmit={(feedback) => {
            // API_TODO: POST /api/feedback/submit
            setUser(prev => ({
              ...prev,
              feedbackReviews: [...(prev.feedbackReviews || []), { ...feedback, user: prev.name }],
            }));
            setShowFeedback(false);
          }}
          onDismiss={() => setShowFeedback(false)}
        />
      )}
    </div>
  );
}
